import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Checkout.css";
import { Zoom } from "react-awesome-reveal";
import OrderSummary from "../checkout/OrderSummary";
import SlotBooking from "../checkout/SlotBooking";
import axios from "axios";
import {
  updateBasketAttributes,
  fetchBasketCheckoutUrl,
} from "./../../lib/ShopifyUtility";
import Dialog from "../Dialog";
import ProceedToPaymentModal from "../ProceedToPaymentModal";

export default function Checkout({
  basket,
  removeFromBasket,
  updateItemBasketQuantity,
}) {
  const minOrderPrice = 60;
  const [underMinOrderPrice, setUnderMinOrderPrice] = useState(true);
  const [noAvailableSlots, setNoAvailableSlots] = useState(true);
  const [blockProceedToPayment, setBlockProceedToPayment] = useState(true);

  const [notes, setNotes] = useState("");
  const [slot, setSlot] = useState(null);
  const [reservedSlot, setReservedSlot] = useState(null);
  const [reservedSlotLoading, setReservedSlotLoading] = useState(true);
  const [slotTaken, setSlotTaken] = useState(false);
  const [takenSlot, setTakenSlot] = useState("");
  const [invalidFields, setInvalidFields] = useState([]);

  const [isOpen,setIsOpen] = useState(false);
  const [isLoadingRedirect, setIsLoadingRedirect] = useState(false);

  function openModal() {
    setIsOpen(true);
  }

  let navigate = useNavigate();

  //this needs changed
  const closeModal = useCallback(() => {
    setIsOpen(false)
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scrollToTopSmoothly()
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const apiKey = process.env.REACT_APP_CHATTERS_API_KEY;
  const apiUri = process.env.REACT_APP_CHATTERS_API_URI;

  const getDates = async (start, end) => {
    try {
      setReservedSlotLoading(true);

      const response = await axios.get(
        `${apiUri}/delivery-slots/get-dates/${start}/${end}?basketId=${basket.id}`,
        {
          headers: {
            Authorization: apiKey,
          },
        }
      );

      const { available, reservedSlot } = response.data;

      setReservedSlot(reservedSlot);

      // If a reserved slot exists, set it as the selected slot
      if (reservedSlot && reservedSlot.date) {
        setSlot(reservedSlot.date);
      }

      return available;
    } catch (error) {
      console.error("Error fetching data:", error.message);
    } finally {
      setReservedSlotLoading(false);
    }
  };

  const checkSlotAvailability = async () => {
    try {
      const slotObj = convertSlotForRequests(slot);

      const response = await axios.post(
        `${apiUri}/delivery-slots/check-available`,
        {
          date: slotObj.date,
          slotWindow: slotObj.slotWindow,
          basketId: basket.id,
        },
        {
          headers: {
            Authorization: apiKey,
          },
        }
      );

      return response.data.available;
    } catch (error) {
      console.error("Error checking slot availability:", error.message);
      return false;
    }
  };

  const reserveSlot = async () => {
    try {
      const slotObj = convertSlotForRequests(slot);

      const response = await axios.put(
        `${apiUri}/delivery-slots/reserve-slot`,
        {
          date: slotObj.date,
          slotWindow: slotObj.slotWindow,
          reservedBasketId: basket.id,
        },
        {
          headers: {
            Authorization: apiKey,
          },
        }
      );

      return response.data.success;
    } catch (error) {
      console.error("Error reserving slot:", error.message);
      return false;
    }
  };

  const validateForms = () => {
    let valid = true;
    const invalidFields = [];

    if (!slot) {
      invalidFields.push("Pick a slot");
      valid = false;
    }

    setInvalidFields(invalidFields);
    return valid;
  };

  const scrollToTopSmoothly = () => {
    const scrollToTop = () => {
      const currentPosition = window.scrollY;
      if (currentPosition > 0) {
        const scrollDistance = Math.max(50, currentPosition / 50);
        window.scrollBy(0, -scrollDistance);
        requestAnimationFrame(scrollToTop);
      }
    };

    scrollToTop();
  };

  const convertSlotForRequests = (slotInput) => {
    const splitSlot = slotInput.split(" ");
    const date = splitSlot[0];
    const slotWindow = splitSlot[1];

    return { date: date, slotWindow: slotWindow };
  };

  const hasReservedSlotExpired = (expiresAt) => {
    if (!expiresAt) return true;

    const expiryTime = new Date(expiresAt).getTime();
    const now = Date.now();

    return now > expiryTime;
  };

  const onProceedToPaymentConfirm = async () => {
    try {
      // Final check including basketId awareness
      const isAvailable = await checkSlotAvailability();
      if (!isAvailable) {
        closeModal();
        setSlotTaken(true);
        setTakenSlot(slot);
        setSlot("");
        scrollToTopSmoothly();
        return;
      }

      if (reservedSlot && hasReservedSlotExpired(reservedSlot.expiresAt)) {
        closeModal();
        setSlotTaken(true);
        setTakenSlot(slot);
        setSlot("");
        scrollToTopSmoothly();
        return;
      }

      // Attempt to reserve the slot
      if (!reservedSlot) {
        const reservationSuccess = await reserveSlot();
        if (!reservationSuccess) {
          closeModal();
          setSlotTaken(true);
          setTakenSlot(slot);
          setSlot("");
          scrollToTopSmoothly();
          return;
        }

        // Add slot details to basket attributes
        const attributes = [
          {
            key: "Collection Slot",
            value: formatDateTimeSlot(slot),
          },
          {
            key: "Basket ID",
            value: basket.id,
          },
          ...(notes && [
            {
              key: "Customer Notes",
              value: notes,
            },
          ]),
        ];

        await updateBasketAttributes(basket.id, attributes);
      }

      const checkoutUrl = await fetchBasketCheckoutUrl(basket.id);
      if (checkoutUrl) {
        setIsLoadingRedirect(true);
        window.location.href = checkoutUrl;
      } else {
        console.error("Checkout URL could not be retrieved.");
      }
    } catch (error) {
      console.error("Error during checkout process:", error);
    }
  };

  useEffect(() => {
    if (slotTaken) {
      closeModal();
      setSlot("");
    }
  }, [slotTaken, closeModal]);

  useEffect(() => {
    if (basket?.estimatedCost?.subtotalAmount?.amount < minOrderPrice) {
      setUnderMinOrderPrice(true);
    } else {
      setUnderMinOrderPrice(false);
    }
  }, [basket, minOrderPrice]);

  const updateBlockProceedToPayment = useCallback(() => {
    if (underMinOrderPrice || noAvailableSlots || !slot) {
      setBlockProceedToPayment(true);
    } else {
      setBlockProceedToPayment(false);
    }
  }, [underMinOrderPrice, noAvailableSlots, slot]);

  useEffect(() => {
    updateBlockProceedToPayment();
  }, [updateBlockProceedToPayment]);

  const handleFormSubmit = () => {
    setInvalidFields([]);

    // Perform validation
    if (!validateForms()) {
      scrollToTopSmoothly();
      return;
    }

    openModal();
  };

  const formatDateTimeSlot = (slot) => {
    const [datePart, timePart] = slot.split(" ");

    const [year, month, day] = datePart.split("-");
    const slotDate = new Date(year, month - 1, day);

    // Format the date and time
    return `${timePart} ${slotDate.toLocaleDateString([], {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })}`;
  };

  const formatReservedSlotDate = (dateString) => {
    const [rawDate, ...timeParts] = dateString.split(" ");
    const timeWindow = timeParts.join(" "); // Handles cases like "9-11am"

    const date = new Date(rawDate);
    const options = { day: "2-digit", month: "short", year: "numeric" };
    const formattedDate = date.toLocaleDateString("en-GB", options);

    return `${timeWindow} on ${formattedDate}`;
  };

  const formatReservationExpiryTime = (date) => {
    const optionsTime = { hour: "2-digit", minute: "2-digit", hour12: false }; // 24-hour time format

    const formattedTime = new Date(date).toLocaleTimeString(
        "en-GB",
        optionsTime
    );

    return `${formattedTime}`;
  };

  return (
    <>
      <div className="checkout_body">
        <div className="continue_shop_container">
          <Zoom triggerOnce={true}>
            <button className="continue_shop_btn" onClick={() => navigate(-1)}>
              &#8678; CONTINUE ORDER
            </button>
          </Zoom>
        </div>
        <Zoom triggerOnce={true}>
          <h2>CHECKOUT</h2>
        </Zoom>
        <div className="break" />
        {underMinOrderPrice && (
          <Zoom triggerOnce={true}>
            <div
              className="checkout_content"
              style={{ border: "none" }}
            >
              <Dialog
                boldText={`Minimum order subtotal must be £${minOrderPrice}`}
                regularTextAfter={"- please add more items to your basket."}
                dialogType={"error"}
              />
            </div>
          </Zoom>
        )}
        {invalidFields.length !== 0 && (
          <Zoom triggerOnce={true}>
            <div
              className="checkout_content"
              style={{ border: "none" }}
            >
              <Dialog
                boldText={"Missing or invalid fields:"}
                regularTextAfter={invalidFields
                  .map((field) => field.toString())
                  .join(", ")}
                dialogType={"error"}
              />
            </div>
          </Zoom>
        )}
        {slotTaken && (
          <Zoom triggerOnce={true}>
            <div
              className="checkout_content"
              style={{ border: "none" }}
            >
              <Dialog
                boldText={formatDateTimeSlot(takenSlot)}
                regularTextAfter={"Please select another."}
                regularTextBefore={
                  "Sorry this slot is no longer available:"
                }
                dialogType={"error"}
              />
            </div>
          </Zoom>
        )}
        <div className="checkout_container">
          <Zoom triggerOnce={true}>
            <div className="checkout_content">
              <SlotBooking
                getDates={getDates}
                basket={basket}
                slot={slot}
                reservedSlot={reservedSlot}
                reservedSlotLoading={reservedSlotLoading}
                formatReservedSlotDate={formatReservedSlotDate}
                formatReservationExpiryTime={formatReservationExpiryTime}
                notes={notes}
                setSlot={setSlot}
                setNotes={setNotes}
                invalidFields={invalidFields}
                slotTaken={slotTaken}
                setNoAvailableSlots={setNoAvailableSlots}
              />
            </div>
          </Zoom>
          <Zoom triggerOnce={true}>
            <div className="checkout_content">
              <OrderSummary
                basket={basket}
                removeFromBasket={removeFromBasket}
                updateItemBasketQuantity={updateItemBasketQuantity}
                minOrderPrice={minOrderPrice}
              />
            </div>
          </Zoom>
          <Zoom triggerOnce={true}>
            <button
              type="button"
              className={`proceed_to_payment_btn ${
                blockProceedToPayment ? " inactive" : ""
              }`}
              onClick={() => {
                handleFormSubmit();
              }}
            >
              PROCEED TO PAYMENT
            </button>
          </Zoom>
        </div>
        <ProceedToPaymentModal
          reservedSlot={reservedSlot}
          formatReservedSlotDate={formatReservedSlotDate}
          formatReservationExpiryTime={formatReservationExpiryTime}
          modalIsOpen={isOpen}
          closeModal={closeModal}
          onProceedToPaymentConfirm={onProceedToPaymentConfirm}
          isLoadingRedirect={isLoadingRedirect}
        />
      </div>
    </>
  );
}
